/* eslint-disable react/require-default-props */
import React, {
  ReactElement,
} from 'react';
import {
  Box, Flex, Heading, Stack, Text, useColorModeValue,
} from '@chakra-ui/react';
import ItemCardTop from './itemCardTop';

type ItemCardProps = {
  image?: string
  imageGrayscale?: boolean
  title: string
  subtitle?: string
  description?: string|React.ReactNode
  ImageOverlay?: React.ReactNode;
  price?: number|string|React.ReactNode
  prePriceText?: string
  ActionButton?: React.ReactNode;
  imagePosition?: string
}

/**
 *  Generic Card for listing an item on a list.
 */

const ItemCard = ({
  image, imageGrayscale, title, description = '', price,
  prePriceText, ImageOverlay, ActionButton, subtitle,
  imagePosition,
}: ItemCardProps): ReactElement => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const titleColor = useColorModeValue('gray.800', 'white');

  return (
    <Box
      bg={cardBg}
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
      border="1px solid"
      borderColor={borderColor}
      height="100%"
      transition="all 0.3s ease"
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.1)',
      }}
      position="relative"
    >

      <Stack direction="column" height="100%" spacing={0}>
        <ItemCardTop
          image={image}
          ImageOverlay={ImageOverlay}
          imageGrayscale={imageGrayscale}
          imagePosition={imagePosition}
        />

        <Stack direction="column" p={[5, 5]} flexGrow={1} spacing={[4, 3]}>
          {/* Header Section */}
          <Box>
            <Heading
              as="h3"
              size={['md', 'lg']}
              color={titleColor}
              fontWeight="700"
              lineHeight={['1.3', '1.2']}
              mb={[3, 2]}
              fontSize={['lg', 'md']}
            >
              {title}
            </Heading>
            {subtitle && (
              <Text
                fontSize={['sm', 'sm']}
                color={textColor}
                fontWeight="500"
                mb={[3, 2]}
                lineHeight="1.4"
              >
                {subtitle}
              </Text>
            )}
            {description && (
              <Text
                color={textColor}
                fontSize={['sm', 'sm']}
                lineHeight={['1.6', '1.5']}
                noOfLines={[3, 2]}
              >
                {description}
              </Text>
            )}
          </Box>

          {/* Footer Section */}
          <Box mt="auto">
            {/* Metadata Row */}
            {prePriceText && (
              <Flex
                justifyContent="flex-end"
                alignItems="center"
                mb={[3, 3]}
                minH="24px"
              >
                <Text
                  fontSize={['xs', 'xs']}
                  color={textColor}
                  fontWeight="500"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  {prePriceText}
                </Text>
              </Flex>
            )}

            {/* Price and Action Row */}
            <Flex
              justifyContent="space-between"
              alignItems="center"
              gap={[3, 3]}
            >
              {typeof price === 'string' || typeof price === 'number' ? (
                <Text
                  fontWeight="bold"
                  fontSize={['lg', 'lg']}
                  color={titleColor}
                  lineHeight="1.1"
                >
                  {price}
                </Text>
              ) : (
                <Box>
                  {price}
                </Box>
              )}

              {ActionButton && (
                <Box>
                  {ActionButton}
                </Box>
              )}
            </Flex>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ItemCard;
